import { useMemo } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import SetMaxBidIcon from 'app/assets/icons/arrow-circle.svg'
import Button from 'app/components/base/Button'
import Dialog from 'app/components/base/Dialog'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import TextFieldNumber from 'app/components/fields/TextFieldNumber'
import { getApproxPrice } from 'app/features/Profile/Auction/helpers/auctions.helpers'
import useCurrencyState from 'app/hooks/useCurrencyState'
import useFormErrorHandler from 'app/hooks/useFormErrorHandler'
import Alert from 'app/services/Alert'
import { isErrorHandlerType } from 'app/services/ErrorHandler'
import { useSetAutoBidMutation } from 'app/services/Query/auction/auction.mutation'
import { transformFieldPrice } from 'app/utils/helpers/form.helpers'
import { SetMaxBidSchema } from 'app/validation/schemas/listing/listing.schemas'

interface SetMaxBidDialogProps {
  open: boolean
  onClose: () => void
  auctionId: number
  currency: string
  exchangeRate?: number
  onSuccess?: () => void
  onError?: () => void
}

const SetMaxBidDialog: RFC<SetMaxBidDialogProps> = ({
  open,
  onClose,
  auctionId,
  currency,
  exchangeRate = 1,
  onError,
  onSuccess
}) => {
  const { formatNumber, formatMessage } = useIntl()
  const { currency: userCurrency } = useCurrencyState()

  const methods = useForm<SetMaxBidFV>({
    defaultValues: {
      price: ''
    },
    resolver: yupResolver(SetMaxBidSchema),
    mode: 'all'
  })
  const { isSubmitting, isDirty } = methods.formState
  const currentPrice = useWatch({ control: methods.control, name: 'price' })

  const handleFormError = useFormErrorHandler(methods.setError)

  const { mutateAsync } = useSetAutoBidMutation()

  const showApproxPrice = exchangeRate !== 1

  const convertedApproxPrice = useMemo(() => {
    if (!!currentPrice && showApproxPrice) {
      return getApproxPrice({ price: currentPrice, exchangeRate })
    }

    return 0
  }, [currentPrice, showApproxPrice])

  const approxPrice = formatNumber(convertedApproxPrice, {
    currency: userCurrency,
    style: 'currency'
  })

  const onSubmit = async ({ price }) => {
    try {
      await mutateAsync({ auctionId, price })

      Alert.success(formatMessage({ defaultMessage: 'Success' }))

      onClose()

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      if (isErrorHandlerType(error)) {
        handleFormError(error?.data)
      }

      if (onError) {
        onError()
      }
    }
  }

  return (
    <Dialog
      open={open}
      disableScroll
      titleSx={{ alignSelf: 'center', mb: 2 }}
      title={
        <Stack direction="row" spacing={1}>
          <SetMaxBidIcon />

          <Text variant="h2">
            {formatMessage({
              defaultMessage: 'Set Max Bid'
            })}
          </Text>
        </Stack>
      }
      onClose={onClose}
      actions={
        <Button
          size="small"
          onClick={methods.handleSubmit(onSubmit)}
          loading={isSubmitting}
          disabled={!isDirty}
          sx={{ width: '100%' }}
        >
          {formatMessage({ defaultMessage: 'Submit' })}
        </Button>
      }
      contentSx={{ mb: 2 }}
      actionsSx={{ paddingTop: 0 }}
    >
      <FormProvider {...methods}>
        <Stack spacing={3}>
          <Text variant="body2" sx={{ textAlign: 'center' }} color="text.third">
            {formatMessage({
              defaultMessage:
                'ifnd will bid incrementally for you up to your maximum bid.'
            })}
          </Text>

          <Stack spacing={1}>
            <TextFieldNumber
              name="price"
              variant="currency"
              placeholder="0.00"
              currency={currency}
              decimalScale={2}
              transform={transformFieldPrice}
              {...(showApproxPrice && {
                helperText: formatMessage(
                  { defaultMessage: 'Approx. {approxPrice}' },
                  { approxPrice }
                )
              })}
            />

            <Text
              variant="body2"
              sx={{ textAlign: 'center' }}
              color="text.third"
            >
              {formatMessage({
                defaultMessage:
                  'By clicking Submit you are agreeing to buy this item if your bid is accepted'
              })}
            </Text>
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  )
}

export default SetMaxBidDialog
